import { useMemo, useState } from 'react';
import type { Canvas, CanvasShare, CanvasShareKind, PublicUser } from '@avslack/shared';
import { useCanvasShares, useShareCanvas, useUnshareCanvas } from '../data/canvases';
import { useChannels } from '../data/channels';
import { useUsers } from '../data/users';
import { useGroups } from '../data/workspace';
import { Avatar } from './Avatar';
import { Dialog } from './Dialog';
import { Icon } from './Icon';

/**
 * SZEMELYES canvas megosztasa - felhasznaloval, csatornaval vagy csoporttal.
 *
 * Ugyanaz a dokumentum, nem masolat: a cimzettek egyutt szerkesztik a
 * tulajdonossal. Hogy hol latjak:
 *  - ember / csoport: a sajat jegyzeteikben, a "Velem megosztva" alatt;
 *  - csatorna: a csatorna canvasai kozott.
 *
 * Minden valtozas AZONNAL hat (nincs Mentes gomb) - ahogy a csatornalista
 * szem-kapcsoloinal is.
 */
interface Target {
  kind: CanvasShareKind;
  id: string;
  label: string;
  hint: string;
  user?: PublicUser;
}

export function ShareCanvasDialog({
  canvas,
  me,
  onClose,
}: {
  canvas: Canvas;
  me: PublicUser;
  onClose: () => void;
}) {
  const { data: shares, isLoading } = useCanvasShares(canvas.id);
  const share = useShareCanvas(canvas.id);
  const unshare = useUnshareCanvas(canvas.id);
  const { users, usersById, nameOf } = useUsers();
  const { data: channels } = useChannels();
  const { data: groups } = useGroups();
  const [query, setQuery] = useState('');

  /** Minden lehetseges cim - a nevek feloldasahoz es a keresohoz is. */
  const targets = useMemo<Target[]>(() => {
    const people: Target[] = users
      .filter((user) => user.id !== me.id && user.isActive && !user.isBot && user.role !== 'guest')
      .map((user) => ({
        kind: 'user',
        id: user.id,
        label: nameOf(user),
        hint: 'Saját jegyzeteiben jelenik meg',
        user,
      }));
    // Csak olyan csatorna, ahova irhatok - a szerver is ezt keri.
    const rooms: Target[] = (channels ?? [])
      .filter(
        (channel) =>
          (channel.kind === 'public' || channel.kind === 'private') &&
          channel.membership.isMember &&
          !channel.isArchived,
      )
      .map((channel) => ({
        kind: 'channel',
        id: channel.id,
        label: `#${channel.name ?? 'csatorna'}`,
        hint: 'A csatorna canvasai között jelenik meg',
      }));
    const teams: Target[] = (groups ?? []).map((group) => ({
      kind: 'group',
      id: group.id,
      label: `@${group.handle}`,
      hint: `${group.name} · ${group.memberIds.length} fő`,
    }));
    return [...people, ...rooms, ...teams];
  }, [users, channels, groups, me.id, nameOf]);

  const shared = new Set((shares ?? []).map((item) => `${item.kind}:${item.targetId}`));
  const needle = query.trim().toLocaleLowerCase('hu');
  const matches = needle
    ? targets
        .filter((target) => !shared.has(`${target.kind}:${target.id}`))
        .filter((target) =>
          `${target.label} ${target.hint}`.toLocaleLowerCase('hu').includes(needle),
        )
        .slice(0, 8)
    : [];

  const describe = (item: CanvasShare): Target => {
    const known = targets.find(
      (target) => target.kind === item.kind && target.id === item.targetId,
    );
    if (known) return known;
    // Pl. egy csatorna, aminek mar nem vagyok tagja - a megosztas attol meg el.
    const user = item.kind === 'user' ? usersById.get(item.targetId) : undefined;
    return {
      kind: item.kind,
      id: item.targetId,
      label:
        item.kind === 'user'
          ? nameOf(item.targetId)
          : item.kind === 'channel'
            ? 'Csatorna'
            : 'Csoport',
      hint: '',
      ...(user ? { user } : {}),
    };
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={`„${canvas.title}” megosztása`}
      description="A címzettek ugyanezt a canvast látják és szerkeszthetik veled együtt."
    >
      <label className="mb-2 flex items-center gap-2 rounded-control border border-border bg-surface px-3 py-2 focus-within:border-accent">
        <Icon name="keres" className="text-ink-faint" />
        <input
          type="search"
          autoFocus
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Személy, #csatorna vagy @csoport"
          aria-label="Kivel osztod meg?"
          className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-ink-faint"
        />
      </label>

      {needle && (
        <ul className="mb-4 divide-y divide-border-soft rounded-card border border-border-soft">
          {matches.length === 0 ? (
            <li className="px-3 py-2 text-xs text-ink-faint">Nincs ilyen találat.</li>
          ) : (
            matches.map((target) => (
              <li key={`${target.kind}:${target.id}`}>
                <button
                  type="button"
                  disabled={share.isPending}
                  onClick={() =>
                    share.mutate(
                      { kind: target.kind, targetId: target.id },
                      { onSuccess: () => setQuery('') },
                    )
                  }
                  className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors duration-(--duration-quick) hover:bg-surface-hover disabled:opacity-50"
                >
                  <TargetIcon target={target} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium">{target.label}</span>
                    <span className="block truncate text-2xs text-ink-faint">{target.hint}</span>
                  </span>
                  <span className="shrink-0 text-xs font-semibold text-accent">Megosztás</span>
                </button>
              </li>
            ))
          )}
        </ul>
      )}

      {share.isError && (
        <p role="alert" className="mb-3 text-xs text-danger">
          {share.error instanceof Error ? share.error.message : 'Nem sikerült megosztani.'}
        </p>
      )}

      <h3 className="mb-1.5 text-2xs font-semibold uppercase tracking-wide text-ink-faint">
        Megosztva
      </h3>
      {isLoading ? (
        <p className="text-xs text-ink-faint">Betöltés…</p>
      ) : (shares?.length ?? 0) === 0 ? (
        <p className="text-xs text-ink-muted">
          Még csak te látod. Keress fent egy kollégát, csatornát vagy csoportot.
        </p>
      ) : (
        <ul className="divide-y divide-border-soft rounded-card border border-border-soft">
          {shares!.map((item) => {
            const target = describe(item);
            return (
              <li key={item.id} className="flex items-center gap-2.5 px-3 py-2 text-sm">
                <TargetIcon target={target} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-medium">{target.label}</span>
                  {target.hint && (
                    <span className="block truncate text-2xs text-ink-faint">{target.hint}</span>
                  )}
                </span>
                <button
                  type="button"
                  disabled={unshare.isPending}
                  onClick={() => unshare.mutate(item.id)}
                  title={`Megosztás visszavonása: ${target.label}`}
                  aria-label={`Megosztás visszavonása: ${target.label}`}
                  className="shrink-0 rounded-control px-2 py-1 text-xs text-ink-muted transition-colors duration-(--duration-quick) hover:bg-surface-hover hover:text-danger disabled:opacity-50"
                >
                  Visszavonás
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <p className="mt-4 text-2xs text-ink-faint">
        Továbbosztani, kiemelni és törölni csak te tudod. Ha törlöd a canvast, mindenkinél eltűnik.
      </p>
    </Dialog>
  );
}

function TargetIcon({ target }: { target: Target }) {
  if (target.user) {
    return (
      <Avatar
        userId={target.user.id}
        name={target.label}
        avatarUrl={target.user.avatarUrl}
        size="sm"
        className="shrink-0"
      />
    );
  }
  return (
    <span
      aria-hidden="true"
      className="grid size-6 shrink-0 place-items-center rounded-md bg-surface-sunken text-ink-faint"
    >
      <Icon
        name={target.kind === 'channel' ? 'csatorna' : target.kind === 'group' ? 'tagok' : 'fiok'}
        size="sm"
      />
    </span>
  );
}
